import { useState, useRef, useEffect } from "react";
import {
  MicIcon,
  Volume2Icon,
  ChevronDownIcon,
  AlertTriangleIcon,
  CircleIcon,
} from "./Icons";
import { useMediaDevices } from "../hooks/useMediaDevices";
import { useMicrophonePermission } from "../hooks/useMicrophonePermission";
import { strings } from "../strings";

interface DeviceSelectorProps {
  /** Called when microphone is selected */
  onMicrophoneChange?: (deviceId: string) => void;
  /** Called when speaker is selected */
  onSpeakerChange?: (deviceId: string) => void;
  /** Called when device availability changes */
  onAvailabilityChange?: (
    hasMics: boolean,
    hasSpeakers: boolean,
    hasPermission: boolean,
  ) => void;
  className?: string;
}

/**
 * Dropdown for selecting microphone and speaker devices
 * Styled to match Shadcn DropdownMenu
 */
export function DeviceSelector({
  onMicrophoneChange,
  onSpeakerChange,
  onAvailabilityChange,
  className,
}: DeviceSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    microphones,
    speakers,
    selectedMicrophoneId,
    selectedSpeakerId,
    selectMicrophone,
    selectSpeaker,
    hasPermission: hasDevicePermission,
  } = useMediaDevices();

  const { permissionState } = useMicrophonePermission();

  const hasMics = microphones.length > 0;
  const hasSpeakers = speakers.length > 0;
  const hasNoDevices = !hasMics && !hasSpeakers;
  const hasPermission =
    permissionState === "granted" ||
    (permissionState === "unsupported" && hasDevicePermission);

  // Notify parent of availability changes
  useEffect(() => {
    onAvailabilityChange?.(hasMics, hasSpeakers, hasPermission);
  }, [hasMics, hasSpeakers, hasPermission, onAvailabilityChange]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isOpen]);

  const handleMicSelect = (deviceId: string) => {
    selectMicrophone(deviceId);
    onMicrophoneChange?.(deviceId);
  };

  const handleSpeakerSelect = (deviceId: string) => {
    selectSpeaker(deviceId);
    onSpeakerChange?.(deviceId);
  };

  return (
    <div
      ref={containerRef}
      className={`relative inline-block z-[100] ${className || ""}`}
    >
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={strings.deviceSelector.deviceSettings}
        className={`h-12 w-20 pl-8 pr-8 rounded-full bg-card cursor-pointer flex items-center justify-center text-sm font-medium transition-all shadow-sm ${
          hasNoDevices ? "border border-destructive/50" : "border border-border"
        }`}
      >
        <ChevronDownIcon className="w-4 h-4 mr-6 shrink-0" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-popover text-popover-foreground border border-border rounded-md shadow-md min-w-64 z-[9999] overflow-hidden p-1">
          {/* Microphone Label */}
          <div className="px-2 py-1.5 text-sm font-medium flex items-center gap-2">
            <MicIcon className="w-4 h-4" />
            {strings.deviceSelector.microphone}
          </div>

          {!hasMics ? (
            <div className="p-2 text-sm text-muted-foreground flex items-start gap-2">
              <AlertTriangleIcon className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">
                  {strings.deviceSelector.noDevicesFound}
                </p>
                <p className="text-xs mt-1">
                  {strings.deviceSelector.checkMicPermissions}
                </p>
              </div>
            </div>
          ) : !hasPermission ? (
            <div className="p-2 text-sm text-muted-foreground flex items-start gap-2">
              <AlertTriangleIcon className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">
                  {strings.deviceSelector.micAccessRequired}
                </p>
                <p className="text-xs mt-1">
                  {strings.deviceSelector.grantPermission}
                </p>
              </div>
            </div>
          ) : (
            <div>
              {microphones.map((mic) => (
                <button
                  key={mic.deviceId}
                  type="button"
                  onClick={() => handleMicSelect(mic.deviceId)}
                  className="flex items-center gap-2 w-full py-1.5 px-2 pl-8 border-none bg-transparent cursor-pointer text-left text-sm rounded hover:bg-accent hover:text-accent-foreground relative"
                >
                  <span className="absolute left-2 flex w-3.5 h-3.5 items-center justify-center">
                    {selectedMicrophoneId === mic.deviceId && (
                      <CircleIcon className="w-2 h-2 fill-current" />
                    )}
                  </span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {mic.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Separator */}
          <div className="h-px bg-border -mx-1 my-1" />

          {/* Speaker Label */}
          <div className="px-2 py-1.5 text-sm font-medium flex items-center gap-2">
            <Volume2Icon className="w-4 h-4" />
            {strings.deviceSelector.speaker}
          </div>

          {!hasSpeakers ? (
            <div className="p-2 text-sm text-muted-foreground flex items-start gap-2">
              <AlertTriangleIcon className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">
                  {strings.deviceSelector.noDevicesFound}
                </p>
                <p className="text-xs mt-1">
                  {strings.deviceSelector.checkSpeakerPermissions}
                </p>
              </div>
            </div>
          ) : (
            <div>
              {speakers.map((speaker) => (
                <button
                  key={speaker.deviceId}
                  type="button"
                  onClick={() => handleSpeakerSelect(speaker.deviceId)}
                  className="flex items-center gap-2 w-full py-1.5 px-2 pl-8 border-none bg-transparent cursor-pointer text-left text-sm rounded hover:bg-accent hover:text-accent-foreground relative"
                >
                  <span className="absolute left-2 flex w-3.5 h-3.5 items-center justify-center">
                    {selectedSpeakerId === speaker.deviceId && (
                      <CircleIcon className="w-2 h-2 fill-current" />
                    )}
                  </span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {speaker.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
